import { escapeHtml, safeUrl } from './safeUrls';

const TAGS = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'a']);
const DROP = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'form', 'input', 'button', 'img', 'video', 'audio']);

/** Rebuild a small formatting vocabulary. Never copy authored attributes or styles into the viewer. */
export function sanitizeRichText(html: string): string {
    if (typeof document === 'undefined' || typeof html !== 'string') return '';
    const template = document.createElement('template');
    template.innerHTML = html;
    const render = (node: Node): string => {
        if (node.nodeType === 3) return escapeHtml(node.textContent);
        if (node.nodeType !== 1) return '';
        const el = node as Element;
        const tag = el.tagName.toLowerCase();
        if (DROP.has(tag) || el.classList.contains('ql-ui')) return '';
        const children = Array.from(el.childNodes, render).join('');
        if (!TAGS.has(tag)) return children;
        if (tag === 'br') return '<br>';
        if (tag === 'a') {
            const href = safeUrl(el.getAttribute('href'), 'link');
            return href ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${children}</a>` : children;
        }
        // Quill 2 represents both kinds of list using ol; retain the bullet marker explicitly.
        const list = tag === 'li' && el.getAttribute('data-list') === 'bullet' ? ' data-list="bullet"' : '';
        return `<${tag}${list}>${children}</${tag}>`;
    };
    return Array.from(template.content.childNodes, render).join('');
}

/** Old scenes stay literal text. Rich formatting is opt-in via a separate HTML field. */
export function richTextHtml(text: string | null | undefined, html?: string): string {
    return typeof html === 'string' && typeof document !== 'undefined'
        ? sanitizeRichText(html)
        : escapeHtml(text).replace(/\r?\n/g, '<br>');
}

export const RICH_TEXT_CSS = `
    .storysplat-rich-text { overflow-wrap: anywhere; line-height: 1.5; }
    .storysplat-rich-text p { margin: 0 0 .65em; }
    .storysplat-rich-text > :last-child { margin-bottom: 0; }
    .storysplat-rich-text ul, .storysplat-rich-text ol { padding-left: 1.5em; margin: .5em 0; }
    .storysplat-rich-text li[data-list="bullet"] { list-style-type: disc; }
    .storysplat-rich-text a { color: inherit; text-decoration: underline; pointer-events: auto; }
    .storysplat-rich-text h2 { font-size: 1.4em; margin: .6em 0; }
    .storysplat-rich-text h3 { font-size: 1.2em; margin: .6em 0; }
    .storysplat-rich-text blockquote { border-left: 3px solid currentColor; padding-left: 1em; margin: .6em 0; }
`;
